import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { APIError } from "@/api/client";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAuthStore } from "@/stores/authStore";

// Bietet Chrome/Google das Speichern der neuen Anmeldedaten aktiv an (SPA-Login
// per fetch löst den Dialog sonst nicht aus). No-op außerhalb von Chromium.
async function rememberCredential(id: string, password: string): Promise<void> {
  try {
    const w = window as unknown as {
      PasswordCredential?: new (data: { id: string; password: string }) => Credential;
    };
    if (w.PasswordCredential && navigator.credentials?.store) {
      await navigator.credentials.store(new w.PasswordCredential({ id, password }));
    }
  } catch {
    /* nicht unterstützt oder abgelehnt — egal. */
  }
}

export default function Register() {
  const { t } = useTranslation();
  const { register } = useAuthStore();
  // Name/E-Mail über den Erfolgs-Reload retten (der Reload setzt den State
  // zurück — ohne das stünde in der Danke-Anzeige „Danke, — … an ,").
  const [firstname, setFirstname] = useState(
    () => sessionStorage.getItem("ca_reg_name") ?? ""
  );
  const [lastname, setLastname] = useState("");
  const [company, setCompany] = useState("");
  const [position, setPosition] = useState("");
  const [email, setEmail] = useState(
    () => sessionStorage.getItem("ca_reg_email") ?? ""
  );
  const [password, setPassword] = useState("");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitted] = useState(() => {
    // Nach dem erfolgs-bedington Full-Reload (Chrome-Passwort-Dialog) landen
    // wir wieder hier — Flag einmalig lesen und löschen (Name/E-Mail wurden
    // bereits oben von den State-Initializern gelesen, hier nur aufräumen).
    const flag = sessionStorage.getItem("ca_reg_submitted") === "1";
    sessionStorage.removeItem("ca_reg_submitted");
    sessionStorage.removeItem("ca_reg_name");
    sessionStorage.removeItem("ca_reg_email");
    return flag;
  });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!privacyAccepted) {
      setError(t("auth.privacyRequired"));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await register({
        email,
        password,
        firstname,
        lastname,
        company: company || undefined,
        position: position || undefined,
        privacy_accepted: true,
        marketing_consent: marketingConsent,
      });
      await rememberCredential(email, password);
      // Echte Navigation, damit Chrome „Passwort speichern?" auslöst (SPA-State-
      // Wechsel allein tut das nicht). Flag über sessionStorage, damit die
      // Erfolgs-Anzeige den Reload überlebt. BASE_URL = "/ca/" (vite base) —
      // ohne den Prefix landet der Reload auf einer nackten 404-Route.
      sessionStorage.setItem("ca_reg_submitted", "1");
      sessionStorage.setItem("ca_reg_name", firstname);
      sessionStorage.setItem("ca_reg_email", email);
      window.location.assign(`${import.meta.env.BASE_URL}register`);
    } catch (err) {
      setError(
        err instanceof APIError ? err.detail : t("auth.registerFailed"),
      );
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="relative min-h-screen flex flex-col items-center justify-center px-4">
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>
        <h1 className="text-5xl font-serif font-semibold text-ca-brand tracking-tight mb-2">
          Nudging Bloom
        </h1>
        <p className="text-ca-ink-muted uppercase tracking-widest font-bold text-sm mb-12">
          {t("auth.submittedTagline")}
        </p>
        <div className="bg-ca-card rounded-3xl p-10 w-full max-w-lg border border-ca-surface-border space-y-6 text-ca-surface-ink">
          <p className="text-lg leading-relaxed">
            <Trans
              i18nKey="auth.submittedThanks"
              values={{ name: firstname }}
              components={{
                b: <span className="text-ca-surface-accent font-bold" />,
              }}
            />
          </p>
          <p className="leading-relaxed">
            <Trans
              i18nKey="auth.submittedBody"
              values={{ email }}
              components={{
                m: <span className="font-mono text-ca-surface-ink" />,
                a: (
                  <a
                    href="mailto:mybrainhr@gmail.com"
                    className="text-ca-surface-accent hover:underline"
                  />
                ),
              }}
            />
          </p>
          <p className="text-sm text-ca-surface-muted italic">
            — Franz Hütter, BRAIN-HR
          </p>
          <Link
            to="/login"
            className="block text-center text-sm font-bold uppercase tracking-widest text-ca-surface-muted hover:text-ca-surface-accent transition pt-4"
          >
            {t("auth.backToLogin")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-4 py-12">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      <h1 className="text-5xl font-serif font-semibold text-ca-brand tracking-tight mb-2">
        Nudging Bloom
      </h1>
      <p className="text-ca-ink-muted uppercase tracking-widest font-bold text-sm mb-3">
        {t("auth.registerTagline")}
      </p>
      <p className="text-ca-ink-muted text-sm max-w-md text-center mb-10">
        <Trans
          i18nKey="auth.registerIntro"
          components={{ b: <span className="text-brand-gold font-bold" /> }}
        />
      </p>

      <form
        onSubmit={submit}
        className="bg-ca-card rounded-3xl p-10 w-full max-w-md border border-ca-surface-border space-y-5"
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
              {t("auth.firstname")}
            </label>
            <input
              type="text"
              name="firstname"
              autoComplete="given-name"
              value={firstname}
              onChange={(e) => setFirstname(e.target.value)}
              className="w-full bg-ca-card border border-ca-surface-border rounded-xl px-4 py-3 text-ca-surface-ink outline-none focus:border-ca-accent"
              required
            />
          </div>
          <div>
            <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
              {t("auth.lastname")}
            </label>
            <input
              type="text"
              name="lastname"
              autoComplete="family-name"
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
              className="w-full bg-ca-card border border-ca-surface-border rounded-xl px-4 py-3 text-ca-surface-ink outline-none focus:border-ca-accent"
              required
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
            {t("auth.company")}
          </label>
          <input
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className="w-full bg-ca-card border border-ca-surface-border rounded-xl px-5 py-3 text-ca-surface-ink outline-none focus:border-ca-accent"
          />
        </div>
        <div>
          <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
            {t("auth.position")}
          </label>
          <input
            type="text"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            placeholder={t("auth.positionPlaceholder")}
            className="w-full bg-ca-card border border-ca-surface-border rounded-xl px-5 py-3 text-ca-surface-ink outline-none focus:border-ca-accent placeholder:text-ca-surface-muted"
          />
        </div>
        <div>
          <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
            {t("auth.email")}
          </label>
          <input
            type="email"
            name="email"
            id="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-ca-card border border-ca-surface-border rounded-xl px-5 py-3 text-ca-surface-ink outline-none focus:border-ca-accent"
            required
          />
        </div>
        <div>
          <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
            {t("auth.passwordMin")}
          </label>
          <input
            type="password"
            name="password"
            id="new-password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            className="w-full bg-ca-card border border-ca-surface-border rounded-xl px-5 py-3 text-ca-surface-ink outline-none focus:border-ca-accent"
            required
          />
        </div>

        <label className="flex items-start gap-3 text-sm text-ca-surface-ink cursor-pointer">
          <input
            type="checkbox"
            checked={privacyAccepted}
            onChange={(e) => setPrivacyAccepted(e.target.checked)}
            className="mt-1 accent-ca-accent"
            required
          />
          <span>
            <Trans
              i18nKey="auth.privacyConsent"
              components={{
                a: (
                  <Link
                    to="/datenschutz"
                    target="_blank"
                    className="text-ca-surface-accent hover:underline"
                  />
                ),
              }}
            />
          </span>
        </label>
        <label className="flex items-start gap-3 text-sm text-ca-surface-muted cursor-pointer">
          <input
            type="checkbox"
            checked={marketingConsent}
            onChange={(e) => setMarketingConsent(e.target.checked)}
            className="mt-1 accent-ca-accent"
          />
          <span>{t("auth.marketingConsent")}</span>
        </label>

        {error && <p className="text-verdict-red text-sm">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          className="w-full py-4 bg-ca-accent text-ca-brand font-black uppercase tracking-widest rounded-xl shadow-xl hover:scale-[1.02] disabled:opacity-50 transition-all active:scale-95"
        >
          {busy ? "..." : t("auth.registerButton")}
        </button>
        <p className="text-center text-sm text-ca-surface-muted">
          {t("auth.hasAccount")}{" "}
          <Link to="/login" className="text-ca-surface-accent hover:underline">
            {t("auth.loginLink")}
          </Link>
        </p>
      </form>
    </div>
  );
}
